/**
 * Workout Enrichment Utilities
 *
 * Automatically populate prep/recovery blocks with comprehensive,
 * context-aware templates based on workout type.
 *
 * Uses the detailed templates from workoutBlockTemplates.ts for
 * proper warmup and cooldown protocols.
 */

import type { WorkoutTemplate, TemplateExercise } from './types/program';
import { WARMUP_TEMPLATES, COOLDOWN_TEMPLATES, type BlockTemplate } from './templates/workoutBlockTemplates';

// Custom type for enrichment blocks (uses TemplateExercise instead of ProgramExercise)
interface EnrichmentBlock {
  name: string;
  type: 'prep' | 'strength' | 'conditioning' | 'recovery' | 'assessment' | 'agility' | 'endurance';
  exercises: TemplateExercise[];
  notes?: string;
}

/**
 * Convert BlockTemplate exercises to TemplateExercise format
 */
function convertToTemplateExercises(template: BlockTemplate): TemplateExercise[] {
  return template.block.exercises.map(ex => ({
    name: ex.exerciseName,
    sets: ex.sets,
    reps: typeof ex.reps === 'number' ? ex.reps.toString() : ex.reps,
    duration: typeof ex.reps === 'string' && (ex.reps.includes('sec') || ex.reps.includes('min'))
      ? ex.reps
      : undefined,
    details: ex.notes,
    rpe: ex.rpe?.toString(),
    format: 'bodyweight' as const,
  }));
}

/**
 * Get warmup template for workout type
 */
function getWarmupTemplate(workoutType: string): EnrichmentBlock {
  // Map workout types to template IDs
  const typeMap: Record<string, string> = {
    'strength-lower': 'strength-lower-warmup',
    'strength-upper': 'strength-upper-warmup',
    'strength': 'strength-general-warmup',
    'full-body': 'strength-general-warmup',
    'conditioning': 'cardio-warmup',
    'cardio': 'cardio-warmup',
    'ruck': 'ruck-warmup',
    'swim': 'swim-warmup',
    'circuit': 'cardio-warmup',
  };

  const templateId = typeMap[workoutType] || 'strength-general-warmup';
  const template = WARMUP_TEMPLATES.find(t => t.id === templateId);

  if (template) {
    return {
      name: template.block.name,
      type: 'prep',
      exercises: convertToTemplateExercises(template),
    };
  }

  // Fallback to general strength warmup
  const fallback = WARMUP_TEMPLATES.find(t => t.id === 'strength-general-warmup')!;
  return {
    name: fallback.block.name,
    type: 'prep',
    exercises: convertToTemplateExercises(fallback),
  };
}

/**
 * Get cooldown template for workout type
 */
function getCooldownTemplate(workoutType: string): EnrichmentBlock {
  // Map workout types to template IDs
  const typeMap: Record<string, string> = {
    'strength-lower': 'strength-lower-cooldown',
    'strength-upper': 'strength-upper-cooldown',
    'strength': 'strength-general-cooldown',
    'full-body': 'strength-general-cooldown',
    'conditioning': 'cardio-cooldown',
    'cardio': 'cardio-cooldown',
    'ruck': 'ruck-cooldown',
    'swim': 'swim-cooldown',
    'circuit': 'cardio-cooldown',
    'lower-body': 'strength-lower-cooldown',
    'upper-body': 'strength-upper-cooldown',
  };

  const templateId = typeMap[workoutType] || 'strength-general-cooldown';
  const template = COOLDOWN_TEMPLATES.find(t => t.id === templateId);

  if (template) {
    return {
      name: template.block.name,
      type: 'recovery',
      exercises: convertToTemplateExercises(template),
    };
  }

  // Fallback to general strength cooldown
  const fallback = COOLDOWN_TEMPLATES.find(t => t.id === 'strength-general-cooldown')!;
  return {
    name: fallback.block.name,
    type: 'recovery',
    exercises: convertToTemplateExercises(fallback),
  };
}

// Legacy mappings kept for backwards compatibility but now use the better templates
const SMART_PREP_BLOCKS: Record<string, EnrichmentBlock> = {
  'strength-lower': getWarmupTemplate('strength-lower'),
  'strength-upper': getWarmupTemplate('strength-upper'),
  'conditioning': getWarmupTemplate('conditioning'),
  'ruck': getWarmupTemplate('ruck'),
  'circuit': getWarmupTemplate('circuit'),
  'swim': getWarmupTemplate('swim'),
  'default': getWarmupTemplate('strength'),
};

const SMART_RECOVERY_BLOCKS: Record<string, EnrichmentBlock> = {
  'lower-body': getCooldownTemplate('strength-lower'),
  'upper-body': getCooldownTemplate('strength-upper'),
  'full-body': getCooldownTemplate('strength'),
  'ruck': getCooldownTemplate('ruck'),
  'conditioning': getCooldownTemplate('conditioning'),
  'swim': getCooldownTemplate('swim'),
  'default': getCooldownTemplate('strength'),
};

/**
 * Day classification — distinguishes real training days from rest days,
 * and splits rest into active vs passive.
 *
 *   training      — has at least one main-work block (strength /
 *                   conditioning / assessment / agility / endurance /
 *                   ruck / circuit) with exercises.
 *   active-rest   — no main work, but ships an intentional non-empty
 *                   recovery/mobility block in the SOURCE template
 *                   (e.g. a yoga/mobility flow). Completable + counts.
 *   passive-rest  — nothing meaningful to do. True day off. Neutral.
 *
 * MUST be called on the SOURCE template (before enrichWorkoutTemplate),
 * because enrichment injects prep/recovery blocks that would otherwise
 * make every passive-rest day look like active-rest.
 */
export type DayType = 'training' | 'active-rest' | 'passive-rest';

const MAIN_BLOCK_TYPES = new Set([
  'strength', 'conditioning', 'assessment', 'agility', 'endurance', 'ruck', 'circuit',
]);

// Loose shape — only blocks[].type + blocks[].exercises are read, so we
// accept any template-like object. This avoids coupling to a specific
// WorkoutTemplate definition (workoutGenerator and workoutEnrichment
// each declare their own with slightly different sessionType unions).
type TemplateLike = { blocks?: Array<{ type?: string; exercises?: unknown[] }> } | null | undefined;

export function classifyDayType(template: TemplateLike): DayType {
  if (!template || !Array.isArray(template.blocks) || template.blocks.length === 0) {
    return 'passive-rest';
  }
  const hasMain = template.blocks.some((b) =>
    !!b.type && MAIN_BLOCK_TYPES.has(b.type) && Array.isArray(b.exercises) && b.exercises.length > 0
  );
  if (hasMain) return 'training';
  // No main work — is there a deliberate recovery/mobility block with content?
  const hasRecoveryContent = template.blocks.some((b) =>
    b.type === 'recovery' && Array.isArray(b.exercises) && b.exercises.length > 0
  );
  return hasRecoveryContent ? 'active-rest' : 'passive-rest';
}

/** Convenience: true for both active and passive rest. */
export function isRestDayTemplate(template: TemplateLike): boolean {
  return classifyDayType(template) !== 'training';
}

/**
 * Detect workout type from template blocks
 */
function detectWorkoutType(template: WorkoutTemplate): string {
  const blockTypes = template.blocks.map(b => b.type);
  const blockNames = template.blocks.map(b => b.name.toLowerCase());
  
  // Check for ruck
  if (blockTypes.includes('ruck') || blockNames.some(n => n.includes('ruck'))) {
    return 'ruck';
  }
  
  // Check for circuit
  if (blockTypes.includes('circuit') || blockNames.some(n => n.includes('circuit') || n.includes('amrap') || n.includes('emom'))) {
    return 'circuit';
  }
  
  // Check for conditioning
  if (blockTypes.includes('conditioning') || blockNames.some(n => n.includes('conditioning') || n.includes('cardio') || n.includes('interval'))) {
    return 'conditioning';
  }
  
  // Check for strength focus
  const strengthBlocks = template.blocks.filter(b => b.type === 'strength');
  if (strengthBlocks.length > 0) {
    // Detect if upper or lower body
    const exercises = strengthBlocks.flatMap(b => b.exercises.map(e => e.name.toLowerCase()));
    
    const lowerBodyKeywords = ['squat', 'deadlift', 'lunge', 'leg', 'calf', 'glute'];
    const upperBodyKeywords = ['bench', 'press', 'row', 'pull', 'curl', 'tricep', 'chest', 'shoulder'];
    
    const hasLower = exercises.some(e => lowerBodyKeywords.some(k => e.includes(k)));
    const hasUpper = exercises.some(e => upperBodyKeywords.some(k => e.includes(k)));
    
    if (hasLower && !hasUpper) return 'strength-lower';
    if (hasUpper && !hasLower) return 'strength-upper';
    if (hasLower && hasUpper) return 'full-body';
  }
  
  return 'default';
}

/**
 * Check if a prep block is empty or minimal/generic
 * A warmup is considered minimal if it has 3 or fewer exercises
 * and doesn't include proper mobility/activation work
 */
function isPrepBlockEmpty(block: any): boolean {
  if (block.type !== 'prep') return false;

  // Empty block - definitely replace
  if (!block.exercises || block.exercises.length === 0) return true;

  // 1-2 exercises is too minimal for a proper warmup
  if (block.exercises.length <= 2) return true;

  // Check for generic/minimal warmup patterns
  const exerciseNames = block.exercises.map((e: any) =>
    (e.name || e.exerciseName || '').toLowerCase()
  );

  // Generic warmup indicators
  const genericIndicators = [
    'warm-up', 'warm up', 'dynamic warm-up', 'prep', 'movement prep',
    'easy cardio', 'light jog', 'bike', 'assault bike', 'row'
  ];

  // If most exercises are just generic cardio, replace it
  const genericCount = exerciseNames.filter((name: string) =>
    genericIndicators.some(indicator => name.includes(indicator))
  ).length;

  // If >50% of exercises are generic, replace the whole block
  if (genericCount / block.exercises.length > 0.5) return true;

  // 3 exercises might be okay if they include proper activation
  const hasActivation = exerciseNames.some((name: string) =>
    name.includes('bridge') || name.includes('stretch') ||
    name.includes('mobility') || name.includes('activation') ||
    name.includes('squat') || name.includes('lunge')
  );

  if (block.exercises.length === 3 && !hasActivation) return true;

  return false;
}

/**
 * Check if a recovery block is empty or minimal/generic
 * A cooldown is considered minimal if it has 2 or fewer exercises
 */
function isRecoveryBlockEmpty(block: any): boolean {
  if (block.type !== 'recovery') return false;

  // Empty block - definitely replace
  if (!block.exercises || block.exercises.length === 0) return true;

  // 1-2 exercises is too minimal for proper recovery
  if (block.exercises.length <= 2) return true;

  // Check for generic cooldown patterns
  const exerciseNames = block.exercises.map((e: any) =>
    (e.name || e.exerciseName || '').toLowerCase()
  );

  const genericIndicators = [
    'cool down', 'cooldown', 'stretch', 'recovery', 'walk', 'easy'
  ];

  // If most exercises are just generic, replace
  const genericCount = exerciseNames.filter((name: string) =>
    genericIndicators.some(indicator => name.includes(indicator))
  ).length;

  if (genericCount / block.exercises.length > 0.5) return true;

  return false;
}

/**
 * Enrich a workout template with smart prep/recovery blocks
 * 
 * @param template - The workout template to enrich
 * @param options - Optional configuration
 * @returns Enriched template with populated prep/recovery blocks
 */
export function enrichWorkoutTemplate(
  template: any, // Accept any template format for flexibility
  options: {
    replacePrepIfEmpty?: boolean;
    replaceRecoveryIfEmpty?: boolean;
    forceReplace?: boolean;
  } = {}
): any {
  const {
    replacePrepIfEmpty = true,
    replaceRecoveryIfEmpty = true,
    forceReplace = false,
  } = options;

  // Rest days must NOT receive injected warmup/cooldown blocks. A
  // passive-rest day should stay empty; an active-rest day keeps its
  // own mobility/recovery content as-authored. Without this guard,
  // enrichment bolted a prep + recovery block onto every rest day,
  // rendering it as a phantom micro-workout. Return a clone untouched.
  if (isRestDayTemplate(template)) {
    return JSON.parse(JSON.stringify(template));
  }

  // Detect workout type
  const workoutType = detectWorkoutType(template);

  // Clone the template
  const enrichedTemplate: WorkoutTemplate = JSON.parse(JSON.stringify(template));
  
  // Find prep and recovery blocks
  const prepBlockIndex = enrichedTemplate.blocks.findIndex(b => b.type === 'prep');
  const recoveryBlockIndex = enrichedTemplate.blocks.findIndex(b => b.type === 'recovery');
  
  // Enrich prep block
  if (replacePrepIfEmpty && prepBlockIndex !== -1) {
    const prepBlock = enrichedTemplate.blocks[prepBlockIndex];
    if (forceReplace || isPrepBlockEmpty(prepBlock)) {
      const smartPrepKey = workoutType in SMART_PREP_BLOCKS ? workoutType : 'default';
      enrichedTemplate.blocks[prepBlockIndex] = { ...SMART_PREP_BLOCKS[smartPrepKey] };
    }
  } else if (replacePrepIfEmpty && prepBlockIndex === -1) {
    // No prep block exists, add one at the beginning
    const smartPrepKey = workoutType in SMART_PREP_BLOCKS ? workoutType : 'default';
    enrichedTemplate.blocks.unshift({ ...SMART_PREP_BLOCKS[smartPrepKey] });
  }
  
  // Enrich recovery block
  if (replaceRecoveryIfEmpty && recoveryBlockIndex !== -1) {
    const recoveryBlock = enrichedTemplate.blocks[recoveryBlockIndex];
    if (forceReplace || isRecoveryBlockEmpty(recoveryBlock)) {
      // Determine recovery type based on workout
      let recoveryKey = 'default';
      if (workoutType === 'strength-lower' || workoutType === 'ruck') {
        recoveryKey = 'lower-body';
      } else if (workoutType === 'strength-upper') {
        recoveryKey = 'upper-body';
      } else if (workoutType === 'circuit' || workoutType === 'conditioning') {
        recoveryKey = 'full-body';
      }
      
      enrichedTemplate.blocks[recoveryBlockIndex] = { ...SMART_RECOVERY_BLOCKS[recoveryKey] };
    }
  } else if (replaceRecoveryIfEmpty && recoveryBlockIndex === -1) {
    // No recovery block exists, add one at the end
    let recoveryKey = 'default';
    if (workoutType === 'strength-lower' || workoutType === 'ruck') {
      recoveryKey = 'lower-body';
    } else if (workoutType === 'strength-upper') {
      recoveryKey = 'upper-body';
    } else if (workoutType === 'circuit' || workoutType === 'conditioning') {
      recoveryKey = 'full-body';
    }
    
    enrichedTemplate.blocks.push({ ...SMART_RECOVERY_BLOCKS[recoveryKey] });
  }
  
  return enrichedTemplate;
}

/**
 * Batch enrich multiple workout templates
 */
export function enrichWorkoutTemplates(
  templates: Record<string, WorkoutTemplate>,
  options?: Parameters<typeof enrichWorkoutTemplate>[1]
): Record<string, WorkoutTemplate> {
  const enriched: Record<string, WorkoutTemplate> = {};
  
  for (const [key, template] of Object.entries(templates)) {
    enriched[key] = enrichWorkoutTemplate(template, options);
  }
  
  return enriched;
}

